import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
} from "@mui/material";
import api_endpoints from "../../../api";
import { useApiClientQuery } from "../../../hooks/useApiClient";
import { useCustomTranslation } from "../../../hooks/useCustomTranslation";
import { currencyFormatter, dateTimeReadableSimple } from "../../../utils";

const PromiseReminderHistory = ({ organizationId, donorId, refreshKey = 0 }) => {
  const { t } = useCustomTranslation("Promises List");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    setPage(0);
  }, [organizationId, donorId, refreshKey]);

  const remindersQuery = useApiClientQuery(
    () =>
      api_endpoints.getPromiseReminderLogs({
        organization_id: organizationId,
        donor_id: donorId,
        page: page + 1,
        limit: rowsPerPage,
      }),
    {
      queryKey: [
        "promise_reminder_history",
        organizationId,
        donorId || "all",
        page,
        rowsPerPage,
        refreshKey,
      ],
      enabled: Boolean(organizationId),
    }
  );

  const reminderData = remindersQuery.data || {};
  const reminders = Array.isArray(reminderData)
    ? reminderData
    : Array.isArray(reminderData.reminders)
      ? reminderData.reminders
      : [];
  const total = Array.isArray(reminderData)
    ? reminders.length
    : Number(reminderData.pagination?.total || 0);

  if (remindersQuery.isLoading) {
    return (
      <Box className="flex justify-center py-8">
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (remindersQuery.isError) {
    return (
      <Alert severity="error">
        {t("promise_reminders_load_error", "Unable to load reminder history.")}
      </Alert>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        {t("promise_reminder_history", "Reminder History")}
      </Typography>

      {!reminders.length ? (
        <Typography color="text.secondary">
          {t("no_promise_reminders", "No promise reminders have been sent yet.")}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("sent_at", "Sent At")}</TableCell>
                {!donorId ? <TableCell>{t("donor", "Donor")}</TableCell> : null}
                <TableCell>{t("campaign", "Campaign")}</TableCell>
                <TableCell>{t("recipient", "Recipient")}</TableCell>
                <TableCell align="right">{t("promised", "Promised")}</TableCell>
                <TableCell align="right">{t("amount_left", "Amount Left")}</TableCell>
                <TableCell>{t("reminder_type", "Type")}</TableCell>
                <TableCell>{t("status", "Status")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reminders.map((reminder) => {
                const promise = reminder.campaign_promise || {};
                const promised = Number(
                  reminder.promised_amount ?? promise.promise_amount ?? 0
                );
                const given = Number(
                  reminder.given_amount ?? promise.given_amount ?? 0
                );
                const remaining = Number(
                  reminder.remaining_amount ?? Math.max(0, promised - given)
                );
                const donor = reminder.donor_profile;
                const donorName = donor
                  ? `${donor.first_name || ""} ${donor.last_name || ""}`.trim()
                  : "";

                return (
                  <TableRow key={reminder._id} hover>
                    <TableCell>
                      {dateTimeReadableSimple(reminder.sent_at)}
                    </TableCell>
                    {!donorId ? (
                      <TableCell>{donorName || reminder.recipient_email}</TableCell>
                    ) : null}
                    <TableCell>{reminder.campaign?.name || "-"}</TableCell>
                    <TableCell>{reminder.recipient_email}</TableCell>
                    <TableCell align="right">
                      {currencyFormatter(promised)}
                    </TableCell>
                    <TableCell align="right">
                      {currencyFormatter(remaining)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t(reminder.reminder_type, reminder.reminder_type)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={
                          reminder.status === "sent"
                            ? "success"
                            : reminder.status === "pending"
                              ? "warning"
                              : "error"
                        }
                        label={t(reminder.status, reminder.status)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      )}
    </Paper>
  );
};

export default PromiseReminderHistory;
